// KEEP IN SYNC with ./charts-reference.d.mts.
/**
 * Value-axis contribution of the reference lines.
 *
 * A reference line is NOT a series: it takes no palette slot, appears in no
 * legend and contributes no tooltip row. But it must be inside the domain, or
 * a target above the data is drawn off the top of the plot and the one thing
 * the caller wanted to show is the one thing missing.
 *
 * @param {ReadonlyArray<{y:number}>} [refs]
 * @returns {{min:number,max:number}|null} null when nothing usable was given
 */
export function referenceExtent(refs) {
  let min = Infinity;
  let max = -Infinity;
  for (const r of refs || []) {
    const y = r && Number(r.y);
    // Non-finite is dropped rather than propagated: one NaN would otherwise
    // poison the whole domain and blank the chart.
    if (!Number.isFinite(y)) continue;
    if (y < min) min = y;
    if (y > max) max = y;
  }
  return min === Infinity ? null : { min, max };
}

/**
 * Paint the reference lines. Called AFTER the grid and BEFORE the series, so a
 * line orients the eye without ever covering data.
 *
 * The label is drawn in TEXT tokens, not in the line's tone. That is the same
 * rule the rest of this chart obeys: identity is carried by the coloured mark,
 * and colour is never the only channel.
 *
 * @param {CanvasRenderingContext2D} g
 * @param {ReadonlyArray<import("./types").ChartReferenceLine>} refs
 * @param {{plot:{x:number,y:number,width:number,height:number}, valueAt:(v:number)=>number,
 *          theme:import("./types").ChartTheme}} pc
 */
export function drawReferenceLines(g, refs, pc) {
  for (const r of refs || []) {
    if (!Number.isFinite(Number(r.y))) continue;
    const y = pc.valueAt(Number(r.y));
    // Off-plot lines are skipped rather than clamped: a clamped line would sit
    // on the axis and read as a real value at the edge of the range.
    if (y < pc.plot.y || y > pc.plot.y + pc.plot.height) continue;
    const tone = r.tone || "ink";
    // A tone other than "ink" MEANS something, and colour is never allowed to
    // be the only channel that carries it — the same rule `statusColor` in
    // charts-palette.mjs enforces by refusing to hand back a status colour
    // "without something to label it with". Here there is no throw (a
    // reference line is decorative, not a required signal), so an unlabeled
    // tone just falls back to ink rather than lying with an unexplained colour.
    const stroke =
      tone !== "ink" && !r.label
        ? pc.theme.axis
        : tone === "link"
          ? pc.theme.accent
          : tone === "caution"
            ? pc.theme.status.warning
            : tone === "danger"
              ? pc.theme.status.critical
              : pc.theme.axis;
    g.save();
    g.beginPath();
    g.moveTo(pc.plot.x, y);
    g.lineTo(pc.plot.x + pc.plot.width, y);
    g.lineWidth = 1;
    g.strokeStyle = stroke;
    if ((r.style || "dashed") === "dashed") g.setLineDash([4, 3]);
    g.stroke();
    g.restore();
    if (r.label) {
      g.save();
      g.fillStyle = pc.theme.textDim;
      g.textAlign = "left";
      g.textBaseline = "bottom";
      g.fillText(String(r.label), pc.plot.x + 4, y - 3);
      g.restore();
    }
  }
}
